import React, { FC, useCallback, useEffect, useState } from 'react';
import { TextField } from '@material-ui/core';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import Employee from '../../interfaces/api/Employee';
import useEmployeeAccess from '../../hooks/api/useEmployeeAccess';

interface Props {
  value?: string;
  error?: boolean;
  helperText?: string;
  className?: any;
  onChange: (value?: string) => void;
}

const EmployeeAutocomplete: FC<Props> = React.forwardRef<any, Props>(
  ({ onChange, value, className, helperText }, ref) => {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const { getAll } = useEmployeeAccess();

    const getEmployee = useCallback((employeeId: string) => employees.find((employee) => employee.id === employeeId), [
      employees,
    ]);

    const getEmployeeLabel = useCallback(
      (employeeId: string) => {
        const employee = getEmployee(employeeId);
        return `${employee?.firstname} ${employee?.lastname}`;
      },
      [employees]
    );

    useEffect(() => {
      getAll()
        .then((collection) => {
          setEmployees(collection.items);
          setLoading(false);
        })
        .catch((e) => {
          setError(e.message);
          setLoading(false);
        });
    }, []);

    const handleChange = (event: any, value: string | null) => {
      onChange(value ?? undefined);
    };

    const handleSortEmployee = (employeeA: Employee, employeeB: Employee) => {
      return `${employeeA.firstname} ${employeeA.lastname}`.localeCompare(
        `${employeeB.firstname} ${employeeB.lastname}`
      );
    };

    if (loading) {
      return <Skeleton height={50} />;
    }

    if (error) {
      return <div style={{ color: '#f44336' }}>{error}</div>;
    }

    return (
      <Autocomplete
        ref={ref}
        className={className}
        value={value}
        onChange={handleChange}
        options={employees.sort(handleSortEmployee).map((employee) => employee.id)}
        getOptionLabel={(employeeId) => getEmployeeLabel(employeeId)}
        renderInput={(params) => (
          <TextField {...params} label='Employé en charge (si différent)' variant='outlined' fullWidth />
        )}
      />
    );
  }
);

export default React.memo(EmployeeAutocomplete);
